import {
  FluidColor,
  FluidContextMenuItemType,
  FluidContextMenuLink,
  FluidContextMenuSection,
  FluidIconConfig,
  FluidIconKey,
  FluidMenuItem,
  FluidSize,
  FluidTheme,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { EventEmitter, FunctionalComponent, h } from '@stencil/core';

import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { themeVar } from '../../../utils/themes/theme.variables';

export interface BreadcrumbTrailProps extends FluidFunctionalComponentProps {
  trail: FluidMenuItem[];
  breadcrumbClicked: EventEmitter;
  theme: FluidTheme;
  identifier?: string;
  setRef?: (element: HTMLElement) => void;
}

export interface BreadcrumbLinkProps extends FluidFunctionalComponentProps {
  link: FluidMenuItem;
  theme: FluidTheme;
  trailLength?: number;
  breadcrumbClicked?: EventEmitter;
  identifier?: string;
}

// ================================================================ //
// -- Public Functional Components

/**
 * Breadcrumb Home Link
 */
export const BreadcrumbHomeLink: FunctionalComponent<BreadcrumbLinkProps> = (
  props: BreadcrumbLinkProps
) => {
  const { theme, link, trailLength, breadcrumbClicked, identifier } = props;
  const linkId = getLinkId(link?.route, link?.label);

  if (isNullOrUndefined(link)) return;
  return (
    <div
      class='home-link link-container'
      data-test-id={`${identifier}-${linkId}`}>
      <BreadcrumbLink
        link={link}
        theme={theme}
        breadcrumbClicked={breadcrumbClicked}
        identifier={identifier}
      />
      {trailLength >= 1 && (
        <BreadcrumbIcon theme={theme} identifier={identifier} />
      )}
    </div>
  );
};

/**
 * Breadcrumb Responsive Context Menu
 */
export const BreadcrumbContextMenu: FunctionalComponent<
  BreadcrumbTrailProps
> = (props: BreadcrumbTrailProps) => {
  const { trail, breadcrumbClicked, theme, identifier, setRef } = props;
  const iconConfig: FluidIconConfig = {
    key: FluidIconKey.ELLIPSIS_VERTICAL,
    size: themeVar<FluidSize>(theme, 'icons.default_size'),
  };

  if (!trail.length) return;
  return (
    <div
      key='context-menu-container'
      ref={(el: HTMLElement) => setRef(el)}
      data-test-id={`${identifier}-context-menu-wrapper`}
      class='context-menu-container'
      style={{ display: 'none' }}>
      <fluid-context-menu
        menuSections={getMenuSections(trail)}
        data-test-id={`${identifier}-context-menu`}
        asWrapper={true}
        theme={theme}
        overrideGlobalTheme={true}
        onLinkClicked={(event) => {
          breadcrumbClicked.emit(event.detail as FluidMenuItem);
        }}>
        <fluid-button
          data-test-id={`${identifier}-context-menu-button`}
          label='Open Context Menu'
          theme={theme}
          overrideGlobalTheme={true}>
          <fluid-icon
            slot='icon-self'
            class='context-menu-icon'
            config={iconConfig}
            data-test-id={`${identifier}-context-menu-icon`}
            theme={theme}
            overrideGlobalTheme={true}
          />
        </fluid-button>
      </fluid-context-menu>
      <BreadcrumbIcon theme={theme} />
    </div>
  );
};

/**
 * Breadcrumb Trail (i.e. links between "home" & "current page")
 */
export const BreadcrumbTrail: FunctionalComponent<BreadcrumbTrailProps> = (
  props: BreadcrumbTrailProps
) => {
  const { trail, theme, breadcrumbClicked, identifier, setRef } = props;

  if (!trail.length) return;
  return (
    <div
      key='trail-container'
      class='trail-container'
      ref={(el: HTMLElement) => setRef(el)}>
      {trail.map((link: FluidMenuItem) => {
        const linkId = getLinkId(link?.route, link?.label);
        return (
          <div
            class='link-container'
            data-test-id={`${identifier}-${linkId}-container`}>
            <BreadcrumbLink
              link={link}
              theme={theme}
              breadcrumbClicked={breadcrumbClicked}
              identifier={identifier}
            />
            <BreadcrumbIcon
              theme={theme}
              identifier={`${identifier}-${linkId}-icon`}
            />
          </div>
        );
      })}
    </div>
  );
};

/**
 * Breadcrumb Chevron Icon (separates each link in "trail")
 */
export const BreadcrumbIcon: FunctionalComponent<{
  theme: FluidTheme;
  identifier?: string;
}> = ({ theme, identifier }) => {
  const iconConfig: FluidIconConfig = {
    key: FluidIconKey.CHEVRON_RIGHT,
    size: themeVar<FluidSize>(theme, 'icons.default_size'),
    color: FluidColor.OPAQUE,
  };

  return (
    <fluid-icon
      class='separator-icon'
      data-test-id={`${identifier}-separator`}
      config={iconConfig}
      theme={theme}
      overrideGlobalTheme={true}
    />
  );
};

/**
 * Text label for the name of the current page.
 * Displayed as the last item in the Breadcrumb trail.
 */
export const BreadcrumbCurrentPage: FunctionalComponent<BreadcrumbLinkProps> = (
  props: BreadcrumbLinkProps
) => {
  const { theme, link, identifier } = props;
  const linkId = getLinkId(link?.route, link?.label);

  if (isNullOrUndefined(link)) return;
  return (
    <fluid-text
      class='current-page'
      strong={true}
      theme={theme}
      overrideGlobalTheme={true}
      data-test-id={`${identifier}-${linkId}-label`}>
      {link.label}
    </fluid-text>
  );
};

/**
 * Single link in the Breadcrumb trail.
 */
export const BreadcrumbLink: FunctionalComponent<BreadcrumbLinkProps> = (
  props: BreadcrumbLinkProps
) => {
  const { theme, link, breadcrumbClicked, identifier } = props;
  const linkId = getLinkId(link?.route, link?.label);

  if (isNullOrUndefined(link)) return;
  return (
    <fluid-link
      class='breadcrumb-link'
      route={link.route}
      inline={true}
      theme={theme}
      overrideGlobalTheme={true}
      onLinkClicked={() => breadcrumbClicked.emit(link)}
      identifier={`${identifier}-${linkId}`}
      data-test-id={`${identifier}-${linkId}-nav`}>
      {link.label}
    </fluid-link>
  );
};

// ================================================================ //
// -- Functional Component Utilities (Private)

/**
 * Helper function to get linkId
 * @param route - FluidMenuItem "route" property
 * @param label - FluidMenuItem "label" property
 */
const getLinkId = (route: string, label: string): string => {
  if (!route && !label) return;
  const routeStr = route?.startsWith('/') ? route.slice(1) : route;
  return routeStr || label;
};

/**
 * Generates array of FluidContextMenuSection objects from Breadcrumb
 * trail. Used to populate responsive Context Menu.
 * @param trail - array of FluidMenuItem objects from "trail" Prop
 */
const getMenuSections = (trail: FluidMenuItem[]): FluidContextMenuSection[] => {
  return [
    {
      type: FluidContextMenuItemType.LINK,
      links: trail as FluidContextMenuLink[],
    },
  ];
};
